import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { Typography } from '@kizen/kds/Typography';
import { Button } from '@kizen/kds/Button';
import { KizenTypography } from '__app/typography';
import { SettingsContentCard } from '__components/Card/Card';
import Loader from '__components/Kizen/Loader';
import { Chat, type MessageType } from 'ts-components/Chat';
import KnowledgeService from 'services/KnowledgeService';
import { getOriginalError } from '__services/AxiosService';
import type { KnowledgeCatalogAskCitation } from '@kizen/api/knowledge';
import { SourceCitationsTable } from './SourceCitationsTable';

const EMPTY_QUICK_RESPONSES = {
  options: [],
  selectType: 'single_select' as const,
};

const StyledCard = styled(SettingsContentCard)`
  margin-top: 20px;
  padding: 20px 20px 0px 0px;
  display: flex;
  flex-direction: column;
  height: 650px;
  min-height: 400px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  margin-bottom: 15px;
  flex-shrink: 0;
`;

const ToolbarTitle = styled(KizenTypography)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const SplitLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 20px;
  min-height: 0;
`;

const ChatPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 35%;
  overflow: hidden;
  border-right: 1px solid var(--color-border-medium-light);
  min-height: 0;
`;

const CitationsPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 10px;
  flex-shrink: 0;
`;

export const CatalogPreviewChat = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();

  const initialMessage = useMemo<MessageType>(
    () => ({
      id: 'initial-message',
      isUserMessage: false,
      isResponseLoading: false,
      messageContent: t(
        "Hello, I'm Kizen's Support agent, how can I help you today?"
      ),
      role: 'assistant',
    }),
    [t]
  );

  const [messages, setMessages] = useState<MessageType[]>(() => [
    initialMessage,
  ]);
  const [citations, setCitations] = useState<KnowledgeCatalogAskCitation[]>([]);
  const [isResetting, setIsResetting] = useState(false);
  const [hasSentMessage, setHasSentMessage] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const { mutate: askQuestion, isPending } = useMutation({
    mutationFn: (question: string) => {
      abortControllerRef.current = new AbortController();
      return KnowledgeService.askKnowledgeCatalog(
        catalogId,
        { query: question },
        { signal: abortControllerRef.current.signal }
      );
    },
    onSuccess: (data) => {
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => m.id !== 'assistant-loading');
        return [
          ...withoutLoading,
          {
            id: `assistant-${Date.now()}`,
            isUserMessage: false,
            isResponseLoading: false,
            messageContent: data.answer,
            role: 'assistant' as const,
          },
        ];
      });
      setCitations(data.citations);
    },
    onError: (err: any) => {
      if (err?.code === 'ERR_CANCELED') return;
      const responseData = getOriginalError(err);
      const errorMessage =
        responseData?.error ||
        t(
          'An error occurred while processing your question. Please try again.'
        );
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => m.id !== 'assistant-loading');
        return [
          ...withoutLoading,
          {
            id: `assistant-error-${Date.now()}`,
            isUserMessage: false,
            isResponseLoading: false,
            messageContent: errorMessage,
            role: 'assistant' as const,
            isErrorMessage: true,
          },
        ];
      });
    },
  });

  const handleSendMessage = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || isPending) return;

      setHasSentMessage(true);
      setCitations([]);
      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          isUserMessage: true,
          isResponseLoading: false,
          messageContent: trimmed,
          role: 'user' as const,
        },
        {
          id: 'assistant-loading',
          isUserMessage: false,
          isResponseLoading: true,
          messageContent: '',
          role: 'assistant' as const,
        },
      ]);

      askQuestion(trimmed);
    },
    [isPending, askQuestion]
  );

  const handleNewChat = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsResetting(true);
    setHasSentMessage(false);
    setMessages([initialMessage]);
    setCitations([]);
    setIsResetting(false);
  }, [initialMessage]);

  return (
    <>
      <Toolbar>
        <ToolbarTitle as="h2" type="header">
          {t('Preview Chat')}
        </ToolbarTitle>
        <Button
          onClick={handleNewChat}
          data-qa-id="preview-chat-new-chat-button"
        >
          {t('New Chat')}
        </Button>
      </Toolbar>
      <StyledCard>
        <SplitLayout>
          <ChatPanel>
            <Chat
              messages={messages}
              onSendMessage={handleSendMessage}
              onCreateNewChat={handleNewChat}
              isLoadingNewSession={false}
              isMessageResponseLoading={isPending}
              isCompleteSessionLoading={false}
              disableTextAreaMessage={
                hasSentMessage
                  ? t('Restart chat to send a new message.')
                  : undefined
              }
              quickResponseData={EMPTY_QUICK_RESPONSES}
              quickResponsesHeader=""
              selectedQuickResponses={[]}
              onQuickResponseSelect={() => {}}
              alert=""
              onAlert={() => {}}
              onCompleteSession={() => {}}
              placeholder={t('Send a Message')}
              renderMarkdown
              hideControllerButtons
              hideRefresh
            />
          </ChatPanel>
          <CitationsPanel>
            <PanelHeader>
              <Typography size="2xl" weight="semibold">
                {t('Source Citations')}
              </Typography>
            </PanelHeader>
            {isPending || isResetting ? (
              <Loader loading />
            ) : citations.length > 0 ? (
              <SourceCitationsTable
                citations={citations}
                catalogId={catalogId}
              />
            ) : (
              <Typography size="md" color="font-placeholder">
                {t('No citations available.')}
              </Typography>
            )}
          </CitationsPanel>
        </SplitLayout>
      </StyledCard>
    </>
  );
};
